<template>
  <div
    v-if="screenType"
    class="filters-mobile__second-screen"
  >
    <div v-if="screenType?.type === 'categories'">
      <CatalogFiltersMobileCategoriesTree
        v-model:categories-ids="selectedFilters.category"
        where-are-we="catalog"
        :show-all-categories="showAllCategories"
      />
    </div>

    <div
      v-if="screenType?.type === 'price'"
      class="filters-mobile__second-screen__content"
    >
      <div class="price-filter">
        <div class="price-filter__item">
          <span>{{ $t('components.catalog.catalogFiltersMobile.catalogFiltersMobileSecondScreen.priceFrom') }}</span>
          <input
            v-model="selectedFilters.startPrice"
            type="number"
            :placeholder="availableFilters.startPrice"
            min="0"
            step="0.01"
            class="price-filter__item__input"
          >
        </div>
        <div class="price-filter__item">
          <span>{{ $t('components.catalog.catalogFiltersMobile.catalogFiltersMobileSecondScreen.priceTo') }}</span>
          <input
            v-model="selectedFilters.endPrice"
            type="number"
            :placeholder="availableFilters.endPrice"
            min="0"
            step="0.01"
            class="price-filter__item__input"
          >
        </div>
      </div>
    </div>
    <div
      v-else-if="computedFilter"
      class="filters-mobile__second-screen__content"
    >
      <div class="filters-mobile__second-screen__content__sticky-header">
        <UISearchInput
          v-if="computedFilter.hasSearchField || computedFilter.kind === 'COLOR'"
          v-model="computedFilter.searchQuery"
          :placeholder="computedFilter.searchFiledPlaceHolder || $t('components.catalog.catalogFiltersMobile.catalogFiltersMobileSecondScreen.searchFiledPlaceHolder.placeholder')"
          class="filters-mobile__second-screen__search"
        />

        <div
          v-if="screenType.type === 'brand'"
          class="filters-mobile__second-screen__brands-selected"
        >
          <label
            v-for="brandId in selectedFilters[screenType.type]"
            :key="brandId"
            class="filters-mobile__second-screen__brands-selected__item"
          >
            <input
              v-model="selectedFilters[screenType.type]"
              :value="brandId"
              type="checkbox"
              hidden
            >
            {{ computedFilter.allValuesMap.get(`${brandId}`).name }}
            <UIIcon name="cross" />
          </label>
        </div>
      </div>

      <template v-if="screenType?.type === 'brand'">
        <div
          v-for="(brands, keyOfGroup) in brandsByGroups"
          :key="keyOfGroup"
          class="brand-group"
        >
          <span class="brand-group__key">{{ keyOfGroup }}</span>
          <CatalogFiltersMobileSecondScreenRow
            v-for="brand in brands"
            :key="brand.id"
            :attribute="brand"
            :screen-type="screenType"
            :checked="valueIsChecked(brand.id)"
          />
        </div>
      </template>
      <template v-else>
        <CatalogFiltersMobileSecondScreenRow
          v-for="attribute in computedFilter.availableValuesBySearch"
          :key="attribute.id"
          :attribute="attribute"
          :screen-type="screenType"
          :checked="valueIsChecked(attribute.id)"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { TCheckboxQueryParamName } from '~/types/filters';
import {
  useStateAttributes, useStateAvailableFilters,
  useStateSelectedFilters,
} from '~/services/catalogFilter/useAvailableFilters';
import UIIcon from '~/components/ui/UIIcon.vue';
import UISearchInput from '~/components/ui/UISearchInput.vue';
import { useFilters, useFiltersByAttributes } from '~/services/catalogFilter/useCatalogFilters';
import { useBrands } from '~/services/catalogFilter/brands';
import CatalogFiltersMobileCategoriesTree
  from '~/components/catalog/CatalogFiltersMobile/CatalogFiltersMobileCategoriesTree.vue';
import CatalogFiltersMobileSecondScreenRow from './CatalogFiltersMobileSecondScreenRow.vue';

const props = defineProps<{
  screenType: { type: TCheckboxQueryParamName, id: TCheckboxQueryParamName | number } | undefined,
  showAllCategories?: boolean
}>();

const route = useRoute();
const router = useRouter();

const filters = useFilters();
const attributes = useStateAttributes();
const filtersByAttributes = useFiltersByAttributes();
const availableFilters = useStateAvailableFilters();
const selectedFilters = useStateSelectedFilters();

const computedFilter = computed(() => {
  if (props.screenType?.type === 'filter') return filtersByAttributes.value?.[props.screenType.id];

  return filters.value?.[props.screenType.id];
});

function valueIsChecked(id) {
  const filterField = selectedFilters.value?.[props.screenType.type];

  if (!Array.isArray(filterField)) return `${filterField}` === `${id}`;

  return filterField?.includes(id);
}

const { getBrandsByGroups } = useBrands();
const brandsByGroups = computed(() => (props.screenType?.type === 'brand' ? getBrandsByGroups(computedFilter.value.availableValuesBySearch) : []));
</script>

<style scoped lang="scss">
.filters-mobile {
  &__second-screen {
    //height: calc(100vh - 150px);
    overflow-y: auto;
    margin-top: 20px;
    padding: 10px;
    border-top: 1px solid #e0e0e0;

    &__content {
      &__sticky-header {
        position: sticky;
        top: -10px;
        background: #fff;
      }
    }

    &__search {
      height: 40px;
      padding: 0 30px;
      background-position-x: 10px;
      background-size: 14px 15px;
      font-size: 14px;
      color: #333;
      margin-bottom: 10px;
    }

    &__brands-selected {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      padding-bottom: 10px;

      &__item {
        display: flex;
        height: 44px;
        padding: 0 0 0 14px;
        align-items: center;
        justify-content: space-between;
        border: 1px solid #e5e5e5;
        border-radius: 2px;
        color: #000;
        font-size: 14px;
        white-space: nowrap;
        position: relative;

        svg {
          //position: absolute;
          //top: calc(50% - 5px);
          //right: 0;
          //top: 20%;
          //transform: scale(.35) translateY(-30%);
          transform: scale(.35);
        }
      }
    }

    .brand-group {
      &:not(:last-child) {
        margin-bottom: 25px;
      }

      &__key {
        font-weight: 700;
        padding-bottom: 5px;
      }

      &:deep(.filters-value-row:last-child) {
        border-bottom: none;
      }
    }

    .price-filter {
      display: flex;
      width: 100%;
      gap: 10px;

      &__item {
        display: flex;
        flex-direction: column;
        width: 100%;
        font-size: 12px;
        color: #a8a8a8;

        &__input {
          width: 100%;
          height: 30px;
          padding: 0;
          font-size: 14px;
          color: #000;
          border-bottom: 1px solid #f0f0f0;
          outline: none;
          -moz-appearance: textfield;

          &::-webkit-outer-spin-button,
          &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        }
      }
    }
  }
}
</style>
